import { forwardRef } from 'react';

interface TextInputProps {
  type?: string,
  value: string,
  label?: string,
  placeholderText?: string,
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  name?: string,
  infoText?: string,
  isError?: Boolean,
  errorMessage?: string,
  isRequired?: Boolean,
  endAdornment?: React.ReactNode,
  startAdornment?: React.ReactNode,
  helpText?: string,
  isDisabled?: Boolean,
  className?: string,
  labelClassName?: string,
  inputClassName?: string,
  hintClassName?: string,
  isLightThemed?: boolean
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const { type='text', value, label, onChange, onFocus, placeholderText, name, infoText, isError, errorMessage, isRequired, startAdornment, endAdornment, helpText, isDisabled = false, className='', labelClassName='', inputClassName='', hintClassName='', isLightThemed } = props;
  const randomId = Math.floor(100000 + Math.random() * 900000);
  const uniqueId = `${name}-${randomId}`;

  // A tooltip per field is a symptom: it means the label does not explain
  // itself. `infoText` now renders as an inline hint under the field, the same
  // slot `helpText` uses, and the (i) icon is gone.
  const hint = helpText || infoText;

  return (
    <div className="flex flex-col gap-1.5 w-full">
      {label && (
        <label
          htmlFor={uniqueId}
          className={`text-ds-135 font-medium leading-[1.4] ${isLightThemed ? 'text-dark' : 'text-white'} ${labelClassName}`}
        >
          {label}
          {isRequired ? <span className="text-navy-300"> *</span> : null}
        </label>
      )}
      <div
        className={[
          'flex items-center gap-2 rounded-control border transition-colors duration-[120ms]',
          // 44px is the touch target on mobile; 38px is the control height on
          // desktop. The 16px input text is what stops iOS zooming on focus.
          'h-11 px-3 lg:h-[38px]',
          'focus-within:border-action focus-within:shadow-[0_0_0_3px_rgba(32,138,236,0.25)]',
          // One step from the page, not three. A field is a control, not a
          // surface: its border and its focus ring are what make it a field,
          // and at navy-700 it carried the exact tint of a raised card — so a
          // form read as a column of stacked panes rather than as a form.
          isLightThemed ? 'bg-white' : 'bg-navy-800',
          isError ? 'border-state-err' : 'border-navy-400 hover:border-navy-300',
          className,
        ].join(' ')}
      >
        {startAdornment}
        <input
          type={type}
          ref={ref}
          className={[
            'bg-transparent inline-block w-full outline-none border-none',
            'text-[16px] lg:text-ds-135 placeholder:text-navy-300',
            isLightThemed ? 'text-dark' : 'text-white',
            isDisabled ? 'cursor-not-allowed' : '',
            inputClassName,
          ].join(' ')}
          id={uniqueId}
          placeholder={placeholderText}
          name={name}
          value={value}
          onChange={!!isDisabled ? undefined : onChange}
          onFocus={onFocus}
          required={!!isRequired}
          disabled={!!isDisabled}
        />
        {endAdornment}
      </div>
      {!isError && hint ? (
        <span className={`text-ds-115 leading-[1.5] text-navy-200 ${hintClassName}`}>{hint}</span>
      ) : null}
      {isError && errorMessage ? (
        <span className="text-ds-115 leading-[1.5] text-state-err">{errorMessage}</span>
      ) : null}
    </div>
  )
});

export default TextInput;
